import { Fragment, useContext, useMemo, useState } from "react";
import SummaryProps from "./SummaryProps";
import DemographicsSummaryField from "../../../models/serviceModels/DemographicsSummaryField";
import Loader from "../../UI/Loader";
import ApiErrorMessages from "../../UI/ApiErrorMessages";
import './Summary.css';
import { GroupedField, SectionGroupedFields } from "./SectionGroupedFields";
import Button from "../../UI/Button";
import DataVisualization from "./DataVisualization";
import { dataVisualizationsEnabled, participantWorkedWithYearFilterEnabled } from "../../../utils/features";
import { OrganizationContext } from "../../../App";
import {
    getDemographicsRaceMode,
    getDemographicsGenderMode,
    getCombinedRaceEthnicityLabel,
    getDisplayGenderName,
    getEthnicityFieldVisible,
    getRaceFieldLabel,
    isDemographicVisible
} from "../../../utils/demographicsSchema";
import { useReportSummaryQuery } from "../../../hooks/reports/queries/useReportQueries";

const getSummaryFieldType = (field: DemographicsSummaryField): 'gender' | 'ethnicity' | 'race' | undefined => {
    const normalizedFieldName = field.fieldName?.trim().toLowerCase() || '';
    const normalizedFieldLabel = field.fieldLabel?.trim().toLowerCase() || '';
    const normalizedSectionName = field.sectionName?.trim().toLowerCase() || '';
    const combinedText = `${normalizedSectionName} ${normalizedFieldName} ${normalizedFieldLabel}`;

    if (/\bgender\b/.test(combinedText)) {
        return 'gender';
    }

    if (/\bethnicity\b/.test(combinedText)) {
        return 'ethnicity';
    }

    if (/\brace\b/.test(combinedText)) {
        return 'race';
    }

    return undefined;
};

const DemographicsSummary = (props: SummaryProps) => {
    const organization = useContext(OrganizationContext);
    const [showingVisualizations, setShowingVisualizations] = useState<string[]>([]);
    const {
        data: summaries = [],
        isLoading: loading,
        error: apiError
    } = useReportSummaryQuery<DemographicsSummaryField[]>(props.reportId);

    const handleToggleShowingVisualizations = (grouping: GroupedField<DemographicsSummaryField>) => {
        if (showingVisualizations.includes(grouping.name)) {
            setShowingVisualizations([
                ...showingVisualizations.filter(v => v !== grouping.name)
            ]);
        } else {
            setShowingVisualizations([
                ...showingVisualizations,
                grouping.name
            ]);
        }
    }

    const normalizedSummaries = useMemo(() => {
        const showGender = isDemographicVisible(organization.value, 'gender');
        const showEthnicity = getEthnicityFieldVisible(organization.value);
        const showRace = isDemographicVisible(organization.value, 'race');
        const simplifiedGender = getDemographicsGenderMode(organization.value) === 'simplified';
        const combinedRaceMode = getDemographicsRaceMode(organization.value) === 'combined';
        const raceFieldLabel = getRaceFieldLabel(organization.value);

        return summaries.reduce<DemographicsSummaryField[]>((normalized, field) => {
            if (field.fieldName === 'participant_worked_with_year' && !participantWorkedWithYearFilterEnabled) {
                return normalized;
            }

            const fieldType = getSummaryFieldType(field);

            if (fieldType === 'gender' && !showGender) {
                return normalized;
            }

            if (fieldType === 'ethnicity' && !showEthnicity && !(combinedRaceMode && showRace)) {
                return normalized;
            }

            if (fieldType === 'race' && !showRace) {
                return normalized;
            }

            const nextField = { ...field };

            if (fieldType === 'gender' && simplifiedGender && nextField.fieldGrouping) {
                nextField.fieldGrouping = getDisplayGenderName(nextField.fieldGrouping, organization.value);
            }

            if ((fieldType === 'ethnicity' || fieldType === 'race') && combinedRaceMode && showRace) {
                nextField.fieldName = 'race_ethnicity';
                nextField.fieldLabel = raceFieldLabel;
                nextField.fieldGrouping = getCombinedRaceEthnicityLabel(nextField.fieldGrouping) || nextField.fieldGrouping;
            }

            normalized.push(nextField);
            return normalized;
        }, []);
    }, [summaries, organization.value]);

    const groupedFields: SectionGroupedFields<DemographicsSummaryField>[] = useMemo(() => {
        const grouping: SectionGroupedFields<DemographicsSummaryField>[] = [];


        for (const field of normalizedSummaries) {
            let existingSection = grouping.find(g => g.name === field.sectionName);
            if (!existingSection) {
                existingSection = {
                    name: field.sectionName,
                    fields: []
                };
                grouping.push(existingSection);
            }

            let existingGroupedField = existingSection.fields.find(f => f.name === field.fieldName);
            if (!existingGroupedField) {
                existingGroupedField = {
                    name: field.fieldName,
                    label: field.fieldLabel,
                    fields: [field]
                };
                existingSection.fields.push(existingGroupedField);
            } else {
                existingGroupedField.fields.push(field);
            }
        }

        return grouping;
    }, [normalizedSummaries]);

    if (loading) {
        return <Loader />
    }

    const getSummaryDisplay = (grouping: GroupedField<DemographicsSummaryField>) => {
        if (grouping.fields.length === 0) {
            return null;
        }

        const showGraph = showingVisualizations.includes(grouping.name);

        const firstField = grouping.fields[0];

        if (firstField.averageValue === null && firstField.percentageValue === null && firstField.medianValue === null &&
            grouping.fields.length === 1 && firstField.totalValue !== null) {
            return (
                <div className="column-medium-3">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                        <div className="summary-overall-value">
                            {firstField.totalValue}
                        </div>
                    </div>
                </div>
            )
        }
        
        if (firstField.averageValue === null && firstField.percentageValue === null && firstField.medianValue === null &&
            firstField.totalValue === null) {
            return (
                <div className="column-medium-3">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                    </div>
                </div>
            )
        }

        if (firstField.percentageValue === null) {
            return (<div className="column-medium-6">
                <div className="summary">
                    <h4>{grouping.label}</h4>
                    {dataVisualizationsEnabled && <Button id="toggle-visualizations" text="Toggle" onClick={() => handleToggleShowingVisualizations(grouping)} />}
                    {!showGraph && <table className="table">
                        <thead>
                            <tr>
                                <th>total</th>
                                <th>median</th>
                                <th>average</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grouping.fields.map((f, i) => {
                                return <tr>
                                    <td>{f.totalValue}</td>
                                    <td>{f.medianValue && f.medianValue.toFixed(2)}</td>
                                    <td>{f.averageValue && f.averageValue.toFixed(2)}</td>
                                </tr>
                            })}
                            {firstField.totalValue !== null && <tr className="border-top">
                                <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                <td></td>
                                <td></td>
                            </tr>}
                        </tbody>
                    </table>}
                    
                    {showGraph && <DataVisualization data={grouping} />}
                </div>
            </div>);
        }

        if (firstField.medianValue === null) {
            return (
                <div className="column-medium-6">
                    <div className="summary">
                        <h4>{grouping.label}</h4>
                        {dataVisualizationsEnabled && <Button id="toggle-visualizations" text="Toggle" onClick={() => handleToggleShowingVisualizations(grouping)} />}
                        {!showGraph && <table className="table">
                            <thead>
                                <tr>
                                    <th>value</th>
                                    <th>total</th>
                                    <th>percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouping.fields.map((f, i) => {
                                    return <tr>
                                        <td>
                                            <div className="summary-label-cell">{f.fieldGrouping}</div>
                                        </td>
                                        <td>{f.totalValue && f.totalValue}</td>
                                        <td>{f.percentageValue && f.percentageValue.toFixed(2)}%</td>
                                    </tr>
                                })}
                                {firstField.totalValue !== null && <tr className="border-top">
                                    <td></td>
                                    <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                    <td></td>
                                </tr>}
                            </tbody>
                        </table>}
                        
                        {showGraph && <DataVisualization data={grouping} />}
                    </div>
                </div>);
        }

        return (
            <div className="column-medium-6">
                <div className="summary">
                    <h4>{grouping.label}</h4>
                    {dataVisualizationsEnabled && <Button id="toggle-visualizations" text="Toggle" onClick={() => handleToggleShowingVisualizations(grouping)} />}
                    {!showGraph && <table className="table">
                        <thead>
                            <tr>
                                <th>value</th>
                                <th>total</th>
                                <th>percentage</th>
                                <th>median</th>
                                <th>average</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grouping.fields.map((f, i) => {
                                return <tr className="padding-2">
                                    <td>
                                        <div className="fw-100 summary-label-cell">{f.fieldGrouping}</div>
                                    </td>
                                    <td>{f.totalValue && f.totalValue}</td>
                                    <td>{f.percentageValue && f.percentageValue.toFixed(2)}%</td>
                                    <td>{f.medianValue && f.medianValue.toFixed(2)}</td>
                                    <td>{f.averageValue && f.averageValue.toFixed(2)}</td>
                                </tr>
                            })}
                            {firstField.totalValue !== null && <tr className="border-top">
                                <td></td>
                                <td>{grouping.fields.reduce((previous, currentField) => previous + currentField.totalValue!, 0)}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>}
                        </tbody>
                    </table>}
                    
                    {showGraph && <DataVisualization data={grouping} />}
                </div>
            </div>);
    }

    const includesHouseholdMembers = props.parameters?.include_household_members === 'true';

    return <div className="summary-container">
        <h3>Summary for {includesHouseholdMembers ? 'Participant and Household Member' : 'Participant'} Demographics</h3>
        <ApiErrorMessages error={apiError} />
        {groupedFields.map(grouping => {
            return <Fragment key={grouping.name}>
                <h4>{grouping.name}</h4>
                <div className="row">
                    {grouping.fields.map(f => {
                        return <Fragment key={f.name}>
                            {getSummaryDisplay(f)}
                        </Fragment>
                    })}
                </div>
            </Fragment>
        })}
    </div>
}

export default DemographicsSummary;
